import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function FAQ() {
  const faqs = [
    {
      q: "How much does a new roof cost in Saskatchewan?",
      a: "The cost of a new roof depends on several factors: the square footage of your roof, the pitch (steepness), the complexity (number of valleys, dormers, or skylights), and the materials you choose. We provide free, detailed, fixed-price quotes so you know exactly what the project will cost before you sign anything. No hidden fees.",
    },
    {
      q: "How long does a roof replacement take?",
      a: "Most standard residential roof replacements are completed in 1 to 2 days, weather permitting. Larger or more complex roofs may take 3 to 4 days. We prioritize efficiency without compromising quality, and we always ensure your property is cleaned up completely at the end of each workday.",
    },
    {
      q: "Does my insurance cover hail or storm damage?",
      a: "If your roof was damaged by a specific weather event like a hailstorm or severe windstorm, your homeowner's insurance typically covers the cost of replacement or repair, minus your deductible. We can perform a thorough inspection, document the damage with photos, and provide the necessary information to assist with your insurance claim.",
    },
    {
      q: "Can you install a new roof during the winter?",
      a: "While spring, summer, and fall are the peak seasons for roofing, we can and do perform emergency repairs and certain installations during the winter months. We use specialized techniques and materials suited for cold weather application when necessary.",
    },
    {
      q: "How do I know if I need a roof repair or a full replacement?",
      a: "Signs that you might need a replacement include: shingles that are curling, cracking, or missing; excessive shingle granules in your gutters; a roof that is older than 20 years; or widespread leaking. If the damage is isolated to a small area (like a few blown-off shingles or a leak around a chimney), a repair might be sufficient. We offer free inspections to give you an honest assessment.",
    },
    {
      q: "What type of roofing material is best for Saskatchewan's climate?",
      a: "High-quality architectural asphalt shingles are excellent for our climate when installed correctly, offering good wind resistance and durability. Metal roofing is another premium option that performs exceptionally well against hail, snow, and high winds, often lasting 50+ years. We can help you weigh the pros and cons of each for your specific home.",
    },
    {
      q: "Do you offer warranties on your work?",
      a: "Yes. We provide strong workmanship warranties on our installations, in addition to the manufacturer's warranty on the materials. Because we are a local Saskatchewan company, we are here to honor those warranties if you ever have an issue down the road.",
    },
    {
      q: "What is ice damming and how do you prevent it?",
      a: "Ice damming occurs when heat escapes from your attic, melting snow on the roof, which then refreezes at the colder eaves, causing water to back up under the shingles. We prevent this by ensuring your roof has proper attic ventilation, adequate insulation, and by installing high-quality ice and water shield membrane along the eaves and in valleys.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Roofing FAQ Saskatchewan | Sask Roofing Company"
        description="Answers to common questions about roof replacement, repair costs, hail damage, and roofing materials for Saskatchewan homeowners."
        schemaJson={[faqSchema]}
      />

      {/* Hero Section */}
      <section className="relative w-full py-24 md:py-32 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/12727c82-3cda-4f0f-93ca-cddcf164b27c.png&output=webp&q=60"
            alt="Close up of high quality asphalt shingle roof installation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="container relative z-10 px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Frequently Asked <span className="text-accent">Questions</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
              Straight answers to your questions about roof replacements,
              repairs, costs, and protecting your home from Saskatchewan
              weather.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          <Accordion
            type="single"
            collapsible
            className="w-full bg-background rounded-xl border p-2 shadow-sm"
          >
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b-0 px-4"
              >
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary text-primary-foreground text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <div className="container px-4 relative z-10 max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Still Have Questions?
          </h2>
          <p className="text-xl text-primary-foreground/80">
            Reach out to our team. We're happy to discuss your roofing needs and
            provide a free, no-obligation inspection.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Get Your Free Roof Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
